import React from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { AuthProvider, useAuth } from './hooks/useAuth'
import { LanguageProvider } from './lib/language'
import './index.css'

// Pages
import LandingPage from './pages/LandingPage'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import AdminPortal from './pages/AdminPortal'
import NotaryPortal from './pages/NotaryPortal'
import ClientPortal from './pages/ClientPortal'
import IndividualPortal from './pages/IndividualPortal'
import PendingApproval from './pages/PendingApproval'
import ServicesPage from './pages/ServicesPage'
import AboutPage from './pages/AboutPage'
import ContactPage from './pages/ContactPage'
import AccessibilityStatement from './pages/AccessibilityStatement'
import TermsOfService from './pages/TermsOfService'
import PrivacyPolicy from './pages/PrivacyPolicy'
import ResetPasswordPage from './pages/ResetPasswordPage'
import NotaryBioPage from './pages/NotaryBioPage'
import BookingPage from './pages/BookingPage'
import RescissionCalendarLanding from './pages/RescissionCalendarLanding'

// ✅ ERROR BOUNDARY — catches React #62 and any other crashes!!
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }
  componentDidCatch(error, info) {
    console.error('LCA App Error:', error, info)
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight:'100vh', background:'#0D1B3E', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:24 }}>
          <div style={{ width:120, height:120, borderRadius:'50%', border:'1px solid rgba(201,168,76,0.4)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:24 }}>
            <img src="/lca_logo_quill_map.png" alt="Loan Closing Agent LLC" style={{ width:104, height:104, objectFit:'contain' }} />
          </div>
          <div style={{ fontFamily:'Cormorant Garamond, serif', fontSize:28, color:'#C9A84C', marginBottom:12 }}>Loan Closing Agent LLC</div>
          <div style={{ fontSize:14, color:'rgba(255,255,255,0.7)', marginBottom:32, textAlign:'center' }}>
            We're experiencing a brief technical issue. Please try again in a moment!!
          </div>
          <button onClick={() => window.location.reload()} style={{ background:'#C9A84C', color:'#0D1B3E', border:'none', padding:'12px 32px', borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer' }}>
            Refresh Page
          </button>
          <a href="/login" style={{ color:'rgba(255,255,255,0.5)', fontSize:12, marginTop:16, textDecoration:'none' }}>
            Go to Login →
          </a>
        </div>
      )
    }
    return this.props.children
  }
}

function PageLoader() {
  return (
    <div className="page-loader">
      <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, color: '#C9A84C', letterSpacing: '0.06em' }}>
        Loan Closing Agent LLC
      </div>
      <div className="spinner" />
    </div>
  )
}

function ProtectedRoute({ children, allowedRoles }) {
  const { user, profile, loading } = useAuth()
  if (loading) return <PageLoader />
  if (!user) return <Navigate to="/login" replace />
  if (!profile) return <Navigate to="/login" replace />
  // ✅ Only individuals are auto-approved — notaries and clients must be approved by admin
  if (!profile.is_approved && profile.role !== 'individual') return <Navigate to="/pending" replace />
  if (allowedRoles && !allowedRoles.includes(profile.role)) return <Navigate to="/login" replace />
  return children
}

function RoleRouter() {
  const { profile, loading } = useAuth()
  if (loading) return <PageLoader />
  if (!profile) return <Navigate to="/login" replace />
  switch (profile.role) {
    case 'admin':  return <Navigate to="/admin"  replace />
    case 'notary': return <Navigate to="/notary" replace />
    case 'client': return <Navigate to="/client" replace />
    default:       return <Navigate to="/order"  replace />
  }
}

function AppRoutes() {
  const { user, loading } = useAuth()

  // ✅ Public bio page — render immediately without auth check!!
  if (window.location.pathname.startsWith('/bio/')) {
    return (
      <Routes>
        <Route path="/bio/:notaryId" element={<NotaryBioPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    )
  }

  if (loading) return <PageLoader />

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={user ? <RoleRouter /> : <LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/pending" element={<PendingApproval />} />
      <Route path="/accessibility" element={<AccessibilityStatement />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/book"     element={<BookingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/portal" element={<RoleRouter />} />
      <Route path="/bio/:notaryId" element={<NotaryBioPage />} />
      <Route path="/rescission-calendar-2026" element={<RescissionCalendarLanding />} />

      <Route path="/admin/*" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminPortal />
        </ProtectedRoute>
      } />

      <Route path="/notary/*" element={
        <ProtectedRoute allowedRoles={['notary']}>
          <NotaryPortal />
        </ProtectedRoute>
      } />

      <Route path="/client/*" element={
        <ProtectedRoute allowedRoles={['client']}>
          <ClientPortal />
        </ProtectedRoute>
      } />

      {/* ✅ Individual Portal — request is PUBLIC, dashboard is protected */}
      <Route path="/order/request" element={<IndividualPortal />} />
      <Route path="/order/track/:jobId" element={<IndividualPortal />} />
      <Route path="/order/*" element={
        <ProtectedRoute allowedRoles={['individual', 'client']}>
          <IndividualPortal />
        </ProtectedRoute>
      } />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <Toaster position="top-right" toastOptions={{
              style: { fontFamily: 'DM Sans, sans-serif', fontSize: 13 },
              success: { iconTheme: { primary: '#C9A84C', secondary: '#0D1B3E' } }
            }} />
            <AppRoutes />
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </ErrorBoundary>
  )
}
